import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
} from "three";

import { RAILWAY_RENDER_CONFIG } from "../../config/railways.ts";
import type { RuntimeLinearFeature } from "../../world/schema/runtimeTile.ts";
import type { RuntimeRailwayEquipment } from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData, WorldTileId } from "../../world/schema/tile.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface RailwaySurfaceBatch {
  readonly object: Group;
  readonly trackCount: number;
  readonly sleeperCount: number;
  readonly electrifiedTrackCount: number;
  readonly equipmentCount: number;
  readonly renderedEquipmentCount: number;
}

interface TrackSection {
  readonly x: number;
  readonly z: number;
  readonly y: number;
  readonly distanceM: number;
  readonly normalX: number;
  readonly normalZ: number;
}

interface MutableMeshData {
  readonly positions: number[];
  readonly indices: number[];
}

export function createRailwaySurfaceBatch(
  tileId: WorldTileId,
  features: readonly Readonly<RuntimeLinearFeature>[],
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
  detailed: boolean,
  equipment: readonly Readonly<RuntimeRailwayEquipment>[] = [],
): RailwaySurfaceBatch | null {
  const railways = features.filter((feature) =>
    feature.kind === "railway" && feature.points.length >= 2
  );
  if (railways.length === 0) {
    return null;
  }

  const ballast = meshData();
  const sleepers = meshData();
  const rails = meshData();
  const catenaryAndEquipment = meshData();
  let sleeperCount = 0;
  let electrifiedTrackCount = 0;
  const trackSections: TrackSection[][] = [];
  for (const feature of railways) {
    if (feature.railwayTunnel === true) {
      continue;
    }
    const sections = createTrackSections(
      feature.points,
      terrain,
      feature.railwayBridge === true,
    );
    if (sections.length < 2) {
      continue;
    }
    trackSections.push([...sections]);
    if (detailed) {
      addRibbon(
        ballast,
        sections,
        RAILWAY_RENDER_CONFIG.ballastShoulderWidthM,
        0,
        RAILWAY_RENDER_CONFIG.ballastShoulderLiftM,
      );
      addRibbon(
        ballast,
        sections,
        RAILWAY_RENDER_CONFIG.ballastWidthM,
        0,
        RAILWAY_RENDER_CONFIG.ballastLiftM,
      );
      for (const offsetM of [
        -(feature.railwayGaugeM ?? RAILWAY_RENDER_CONFIG.railGaugeM) / 2,
        (feature.railwayGaugeM ?? RAILWAY_RENDER_CONFIG.railGaugeM) / 2,
      ]) {
        addRailRibbon(
          rails,
          sections,
          RAILWAY_RENDER_CONFIG.railHeadWidthM,
          offsetM,
          RAILWAY_RENDER_CONFIG.railLiftM,
          RAILWAY_RENDER_CONFIG.railHeadHeightM,
        );
      }
      sleeperCount += addSleepers(sleepers, sections);
      if (feature.railwayElectrified === "contact_line") {
        addCatenary(catenaryAndEquipment, sections);
        electrifiedTrackCount += 1;
      }
    } else {
      addRibbon(
        ballast,
        sections,
        RAILWAY_RENDER_CONFIG.distantBedWidthM,
        0,
        RAILWAY_RENDER_CONFIG.distantLiftM,
      );
    }
  }

  const renderedEquipmentCount = detailed
    ? addRailwayEquipment(
      catenaryAndEquipment,
      equipment,
      terrain,
      trackSections.flat(),
    )
    : 0;

  const object = new Group();
  object.name = `runtime-railways:${tileId}:${detailed ? "detailed" : "distant"}`;
  object.userData = {
    tileId,
    sourceLayer: "official-grpk-railway-centrelines",
    representation: detailed
      ? "terrain-following-ballast-sleepers-and-standard-gauge-rails"
      : "terrain-following-distant-track-bed",
    featureIds: railways.map((feature) => feature.id),
    railwayEvidenceSourceIds: [...new Set(railways.flatMap((feature) =>
      feature.railwayEvidenceSourceId === undefined
        ? []
        : [feature.railwayEvidenceSourceId]
    ))].sort(),
    equipmentIds: equipment.map((item) => item.id),
  };
  addMesh(
    object,
    ballast,
    detailed ? materials.railway.ballast : materials.railway.distant,
    `${object.name}:ballast`,
  );
  if (detailed) {
    addMesh(
      object,
      sleepers,
      materials.railway.sleeper,
      `${object.name}:sleepers`,
    );
    addMesh(
      object,
      rails,
      materials.railway.steel,
      `${object.name}:rails`,
    );
    addMesh(
      object,
      catenaryAndEquipment,
      materials.railway.catenary,
      `${object.name}:catenary-and-equipment`,
    );
  }
  if (object.children.length === 0) {
    return null;
  }
  return {
    object,
    trackCount: railways.length,
    sleeperCount,
    electrifiedTrackCount,
    equipmentCount: equipment.length,
    renderedEquipmentCount,
  };
}

function createTrackSections(
  points: readonly Readonly<{ x: number; z: number }>[],
  terrain: Readonly<TerrainTileData>,
  bridge: boolean,
): readonly TrackSection[] {
  const dense: { x: number; z: number; distanceM: number }[] = [];
  let distanceM = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
    if (lengthM <= 0.01) {
      continue;
    }
    const steps = Math.max(
      1,
      Math.ceil(lengthM / RAILWAY_RENDER_CONFIG.maximumSegmentLengthM),
    );
    if (dense.length === 0) {
      dense.push({ x: from.x, z: from.z, distanceM });
    }
    for (let step = 1; step <= steps; step += 1) {
      const alpha = step / steps;
      dense.push({
        x: from.x + (to.x - from.x) * alpha,
        z: from.z + (to.z - from.z) * alpha,
        distanceM: distanceM + lengthM * alpha,
      });
    }
    distanceM += lengthM;
  }
  const terrainHeights = dense.map((point) =>
    sampleHeight(terrain, point.x, point.z)
  );
  const firstHeight = terrainHeights[0] ?? 0;
  const lastHeight = terrainHeights.at(-1) ?? firstHeight;
  const totalDistanceM = dense.at(-1)?.distanceM ?? 0;
  return dense.map((point, index) => {
    const previous = dense[Math.max(0, index - 1)] ?? point;
    const following = dense[Math.min(dense.length - 1, index + 1)] ?? point;
    const tangentX = following.x - previous.x;
    const tangentZ = following.z - previous.z;
    const tangentLength = Math.hypot(tangentX, tangentZ) || 1;
    return {
      ...point,
      y: bridge && totalDistanceM > 0
        ? firstHeight + (lastHeight - firstHeight) *
          point.distanceM / totalDistanceM
        : terrainHeights[index] ?? 0,
      normalX: -tangentZ / tangentLength,
      normalZ: tangentX / tangentLength,
    };
  });
}

function addCatenary(
  mesh: MutableMeshData,
  sections: readonly TrackSection[],
): void {
  for (const heightM of [
    RAILWAY_RENDER_CONFIG.catenaryContactHeightM,
    RAILWAY_RENDER_CONFIG.catenaryMessengerHeightM,
  ]) {
    addRibbon(
      mesh,
      sections,
      RAILWAY_RENDER_CONFIG.catenaryWireWidthM,
      0,
      heightM,
    );
  }
  forEachInterpolatedSection(
    sections,
    RAILWAY_RENDER_CONFIG.catenaryDropperSpacingM,
    RAILWAY_RENDER_CONFIG.catenaryDropperSpacingM / 2,
    (section) => {
      addOrientedBox(
        mesh,
        section.x,
        section.y +
          (RAILWAY_RENDER_CONFIG.catenaryContactHeightM +
            RAILWAY_RENDER_CONFIG.catenaryMessengerHeightM) / 2,
        section.z,
        section.normalX,
        section.normalZ,
        RAILWAY_RENDER_CONFIG.catenaryWireWidthM,
        RAILWAY_RENDER_CONFIG.catenaryWireWidthM,
        RAILWAY_RENDER_CONFIG.catenaryMessengerHeightM -
          RAILWAY_RENDER_CONFIG.catenaryContactHeightM,
      );
    },
  );
  forEachInterpolatedSection(
    sections,
    RAILWAY_RENDER_CONFIG.catenaryMastSpacingM,
    RAILWAY_RENDER_CONFIG.catenaryMastSpacingM / 2,
    (section) => {
      const mastX = section.x +
        section.normalX * RAILWAY_RENDER_CONFIG.catenaryMastOffsetM;
      const mastZ = section.z +
        section.normalZ * RAILWAY_RENDER_CONFIG.catenaryMastOffsetM;
      addOrientedBox(
        mesh,
        mastX,
        section.y + RAILWAY_RENDER_CONFIG.catenaryMastHeightM / 2,
        mastZ,
        section.normalX,
        section.normalZ,
        RAILWAY_RENDER_CONFIG.catenaryMastWidthM,
        RAILWAY_RENDER_CONFIG.catenaryMastWidthM,
        RAILWAY_RENDER_CONFIG.catenaryMastHeightM,
      );
      addOrientedBox(
        mesh,
        (mastX + section.x) / 2,
        section.y + RAILWAY_RENDER_CONFIG.catenaryMessengerHeightM + 0.35,
        (mastZ + section.z) / 2,
        section.normalX,
        section.normalZ,
        RAILWAY_RENDER_CONFIG.catenaryMastOffsetM * 1.12,
        RAILWAY_RENDER_CONFIG.catenaryMastWidthM,
        RAILWAY_RENDER_CONFIG.catenaryMastWidthM,
      );
    },
  );
}

function addRailwayEquipment(
  mesh: MutableMeshData,
  equipment: readonly Readonly<RuntimeRailwayEquipment>[],
  terrain: Readonly<TerrainTileData>,
  sections: readonly TrackSection[],
): number {
  let rendered = 0;
  for (const item of equipment) {
    if (item.kind === "train_station_entrance") {
      continue;
    }
    const track = nearestTrackSection(item.position.x, item.position.z, sections);
    if (track === null) {
      continue;
    }
    const baseY = sampleHeight(terrain, item.position.x, item.position.z) +
      RAILWAY_RENDER_CONFIG.equipmentLiftM;
    const tangentX = track.normalZ;
    const tangentZ = -track.normalX;
    if (item.kind === "switch") {
      addOrientedBox(
        mesh,
        item.position.x + track.normalX * 0.9,
        baseY + 0.16,
        item.position.z + track.normalZ * 0.9,
        tangentX,
        tangentZ,
        0.72,
        0.42,
        0.24,
      );
    } else if (item.kind === "signal") {
      const postX = item.position.x + track.normalX * 1.65;
      const postZ = item.position.z + track.normalZ * 1.65;
      addOrientedBox(mesh, postX, baseY + 1.85, postZ, tangentX, tangentZ, 0.14, 0.14, 3.7);
      addOrientedBox(mesh, postX, baseY + 3.65, postZ, tangentX, tangentZ, 0.52, 0.24, 0.82);
    } else {
      addOrientedBox(mesh, item.position.x, baseY + 0.72, item.position.z, track.normalX, track.normalZ, 2.45, 0.22, 0.28);
      for (const side of [-0.9, 0.9]) {
        addOrientedBox(
          mesh,
          item.position.x + track.normalX * side,
          baseY + 0.38,
          item.position.z + track.normalZ * side,
          tangentX,
          tangentZ,
          0.18,
          0.18,
          0.76,
        );
      }
    }
    rendered += 1;
  }
  return rendered;
}

function nearestTrackSection(
  x: number,
  z: number,
  sections: readonly TrackSection[],
): TrackSection | null {
  let best: TrackSection | null = null;
  let bestDistanceM = 8;
  for (const section of sections) {
    const distanceM = Math.hypot(section.x - x, section.z - z);
    if (distanceM < bestDistanceM) {
      best = section;
      bestDistanceM = distanceM;
    }
  }
  return best;
}

function forEachInterpolatedSection(
  sections: readonly TrackSection[],
  spacingM: number,
  startM: number,
  visit: (section: TrackSection) => void,
): void {
  const totalDistanceM = sections.at(-1)?.distanceM ?? 0;
  for (let distanceM = startM; distanceM < totalDistanceM; distanceM += spacingM) {
    const section = interpolateSection(sections, distanceM);
    if (section !== null) {
      visit(section);
    }
  }
}

function interpolateSection(
  sections: readonly TrackSection[],
  distanceM: number,
): TrackSection | null {
  const sectionIndex = sections.findIndex((section) =>
    section.distanceM >= distanceM
  );
  if (sectionIndex <= 0) {
    return null;
  }
  const from = sections[sectionIndex - 1]!;
  const to = sections[sectionIndex]!;
  const intervalM = Math.max(0.001, to.distanceM - from.distanceM);
  const alpha = (distanceM - from.distanceM) / intervalM;
  const normalX = from.normalX + (to.normalX - from.normalX) * alpha;
  const normalZ = from.normalZ + (to.normalZ - from.normalZ) * alpha;
  const normalLength = Math.hypot(normalX, normalZ) || 1;
  return {
    x: from.x + (to.x - from.x) * alpha,
    y: from.y + (to.y - from.y) * alpha,
    z: from.z + (to.z - from.z) * alpha,
    distanceM,
    normalX: normalX / normalLength,
    normalZ: normalZ / normalLength,
  };
}

function addOrientedBox(
  mesh: MutableMeshData,
  centerX: number,
  centerY: number,
  centerZ: number,
  longX: number,
  longZ: number,
  lengthM: number,
  widthM: number,
  heightM: number,
): void {
  const first = mesh.positions.length / 3;
  const longLength = Math.hypot(longX, longZ) || 1;
  const lx = longX / longLength;
  const lz = longZ / longLength;
  const sx = -lz;
  const sz = lx;
  const halfLengthM = lengthM / 2;
  const halfWidthM = widthM / 2;
  const halfHeightM = heightM / 2;
  for (const ySign of [-1, 1]) {
    for (const [longSign, shortSign] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as const) {
      mesh.positions.push(
        centerX + lx * halfLengthM * longSign + sx * halfWidthM * shortSign,
        centerY + halfHeightM * ySign,
        centerZ + lz * halfLengthM * longSign + sz * halfWidthM * shortSign,
      );
    }
  }
  mesh.indices.push(
    first, first + 1, first + 2, first, first + 2, first + 3,
    first + 4, first + 6, first + 5, first + 4, first + 7, first + 6,
    first, first + 4, first + 5, first, first + 5, first + 1,
    first + 1, first + 5, first + 6, first + 1, first + 6, first + 2,
    first + 2, first + 6, first + 7, first + 2, first + 7, first + 3,
    first + 3, first + 7, first + 4, first + 3, first + 4, first,
  );
}

function sampleHeight(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): number {
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  return sampleTerrainTileData(
    terrain,
    { x: 0, z: 0 },
    {
      x: Math.min(maximum, Math.max(0, x)),
      z: Math.min(maximum, Math.max(0, z)),
    },
  ) ?? 0;
}

function meshData(): MutableMeshData {
  return { positions: [], indices: [] };
}

function addRibbon(
  mesh: MutableMeshData,
  sections: readonly TrackSection[],
  widthM: number,
  offsetM: number,
  liftM: number,
): void {
  const firstVertex = mesh.positions.length / 3;
  const halfWidthM = widthM / 2;
  for (const section of sections) {
    const centerX = section.x + section.normalX * offsetM;
    const centerZ = section.z + section.normalZ * offsetM;
    mesh.positions.push(
      centerX - section.normalX * halfWidthM,
      section.y + liftM,
      centerZ - section.normalZ * halfWidthM,
      centerX + section.normalX * halfWidthM,
      section.y + liftM,
      centerZ + section.normalZ * halfWidthM,
    );
  }
  for (let index = 1; index < sections.length; index += 1) {
    const previous = firstVertex + (index - 1) * 2;
    const current = firstVertex + index * 2;
    mesh.indices.push(
      previous,
      current,
      previous + 1,
      previous + 1,
      current,
      current + 1,
    );
  }
}

function addRailRibbon(
  mesh: MutableMeshData,
  sections: readonly TrackSection[],
  widthM: number,
  offsetM: number,
  topLiftM: number,
  heightM: number,
): void {
  const firstVertex = mesh.positions.length / 3;
  const halfWidthM = widthM / 2;
  for (const section of sections) {
    const centerX = section.x + section.normalX * offsetM;
    const centerZ = section.z + section.normalZ * offsetM;
    for (const liftM of [topLiftM - heightM, topLiftM]) {
      mesh.positions.push(
        centerX - section.normalX * halfWidthM,
        section.y + liftM,
        centerZ - section.normalZ * halfWidthM,
        centerX + section.normalX * halfWidthM,
        section.y + liftM,
        centerZ + section.normalZ * halfWidthM,
      );
    }
  }
  for (let index = 1; index < sections.length; index += 1) {
    const previous = firstVertex + (index - 1) * 4;
    const current = firstVertex + index * 4;
    mesh.indices.push(
      previous + 2, current + 2, previous + 3,
      previous + 3, current + 2, current + 3,
      previous, previous + 2, current,
      current, previous + 2, current + 2,
      previous + 1, current + 1, previous + 3,
      previous + 3, current + 1, current + 3,
    );
  }
}

function addSleepers(
  mesh: MutableMeshData,
  sections: readonly TrackSection[],
): number {
  const totalDistanceM = sections.at(-1)?.distanceM ?? 0;
  let count = 0;
  for (
    let distanceM = RAILWAY_RENDER_CONFIG.sleeperSpacingM / 2;
    distanceM < totalDistanceM;
    distanceM += RAILWAY_RENDER_CONFIG.sleeperSpacingM
  ) {
    const sectionIndex = sections.findIndex((section) =>
      section.distanceM >= distanceM
    );
    if (sectionIndex <= 0) {
      continue;
    }
    const from = sections[sectionIndex - 1]!;
    const to = sections[sectionIndex]!;
    const intervalM = Math.max(0.001, to.distanceM - from.distanceM);
    const alpha = (distanceM - from.distanceM) / intervalM;
    const x = from.x + (to.x - from.x) * alpha;
    const z = from.z + (to.z - from.z) * alpha;
    const y = from.y + (to.y - from.y) * alpha +
      RAILWAY_RENDER_CONFIG.sleeperLiftM;
    const normalX = from.normalX + (to.normalX - from.normalX) * alpha;
    const normalZ = from.normalZ + (to.normalZ - from.normalZ) * alpha;
    const normalLength = Math.hypot(normalX, normalZ) || 1;
    const nx = normalX / normalLength;
    const nz = normalZ / normalLength;
    const tx = nz;
    const tz = -nx;
    addRectangle(
      mesh,
      x,
      y,
      z,
      nx,
      nz,
      tx,
      tz,
      RAILWAY_RENDER_CONFIG.sleeperLengthM,
      RAILWAY_RENDER_CONFIG.sleeperWidthM,
    );
    count += 1;
  }
  return count;
}

function addRectangle(
  mesh: MutableMeshData,
  centerX: number,
  y: number,
  centerZ: number,
  longX: number,
  longZ: number,
  shortX: number,
  shortZ: number,
  lengthM: number,
  widthM: number,
): void {
  const first = mesh.positions.length / 3;
  const halfLengthM = lengthM / 2;
  const halfWidthM = widthM / 2;
  for (const [longSign, shortSign] of [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ] as const) {
    mesh.positions.push(
      centerX + longX * halfLengthM * longSign +
        shortX * halfWidthM * shortSign,
      y,
      centerZ + longZ * halfLengthM * longSign +
        shortZ * halfWidthM * shortSign,
    );
  }
  mesh.indices.push(first, first + 1, first + 2, first, first + 2, first + 3);
}

function addMesh(
  group: Group,
  data: Readonly<MutableMeshData>,
  material: CityMaterialLibrary["railway"][keyof CityMaterialLibrary["railway"]],
  name: string,
): void {
  if (data.indices.length === 0) {
    return;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(data.positions, 3),
  );
  geometry.setIndex(data.indices);
  geometry.computeVertexNormals();
  geometry.name = name;
  const mesh = new Mesh(geometry, material);
  mesh.name = name;
  mesh.receiveShadow = true;
  group.add(mesh);
}
